import { IListing, Listing } from '@curbnturf/entities';
import * as SiteActions from '@curbnturf/site/src/lib/+state/site.actions';
import { createReducer, on } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import * as ListingActions from './listing.actions';

/**
 * Interface for the 'Listing' data used in
 *  - ListingState, and
 *  - listingReducer
 */
export interface ListingState {
  listings: IListing[];
  selected?: string | number;
  error?: string;
  loaded: boolean;
}

export const initialState: ListingState = {
  listings: [],
  loaded: false,
};

export const listingReducer = createReducer(
  initialState,
  on(ListingActions.listingUpdated, (state, action) => {
    if (!action.listing || (!action.listing.id && !action.listing.syncId)) {
      return state;
    }

    const listings = state.listings.slice();

    const foundIndex = listings.findIndex(
      (el) => el.id === action.listing.id || (action.listing.id === undefined && el.syncId === action.listing.syncId),
    );
    if (foundIndex !== -1) {
      let listing = new Listing({ ...state.listings[foundIndex], ...action.listing });
      listings.splice(foundIndex, 1, listing);
    } else {
      listings.push(new Listing({ ...action.listing }));
    }

    return {
      ...state,
      listings,
    };
  }),
  on(ListingActions.listingRestored, (state, action) => {
    if (!action.listing) {
      return state;
    }

    const listing = action.listing;

    const listingState = {
      ...state,
      listings: { ...state.listings },
      error: undefined,
    };

    // Find listing by ID first then by SyncId, Replace with the updated
    const foundIndex = listingState.listings.findIndex(
      (el) =>
        (el.id && listing.id && el.id === listing.id) || (el.syncId && listing.syncId && el.syncId === listing.syncId),
    );
    if (foundIndex !== -1) {
      listingState.listings.splice(foundIndex, 1, listing);
    } else {
      listingState.listings.push(listing);
    }

    return listingState;
  }),
  on(ListingActions.listingLoaded, (state, action) => {
    if (!action.listing) {
      return state;
    }

    const listing = action.listing;

    const listingState: ListingState = {
      ...state,
      listings: [...state.listings],
      error: undefined,
    };

    // Find listing by ID first then by SyncId, Replace with the updated
    const foundIndex = listingState.listings.findIndex(
      (el) =>
        (el.id && listing.id && el.id === listing.id) || (el.syncId && listing.syncId && el.syncId === listing.syncId),
    );
    if (foundIndex !== -1) {
      listingState.listings.splice(foundIndex, 1, listing);
    } else {
      listingState.listings.push(listing);
    }

    return listingState;
  }),

  on(ListingActions.listingImagesUpdated, (state, action) => {
    if (!action.photos || (!action.id && !action.syncId)) {
      return state;
    }

    const id = action.id;
    const syncId = action.syncId;
    const photos = action.photos;

    const listingState: ListingState = {
      ...state,
      listings: [...state.listings],
      error: undefined,
    };

    // Find listing by ID first then by SyncId, Replace with the updated
    const foundIndex = listingState.listings.findIndex(
      (el) => (el.id && id && el.id === id) || (el.syncId && syncId && el.syncId === syncId),
    );
    if (foundIndex !== -1) {
      const listing: IListing = {
        ...listingState.listings[foundIndex],
      };

      listing.photos = photos;
      listingState.listings.splice(foundIndex, 1, listing);
    }

    return listingState;
  }),

  on(ListingActions.listingsLoaded, (state, action) => {
    const listings: IListing[] = state.listings.slice();
    action.listings.forEach((listing) => {
      if (listing && (listing.id || listing.syncId)) {
        const foundIndex = listings.findIndex(
          (el) =>
            (el.id && listing.id && el.id === listing.id) ||
            (el.syncId && listing.syncId && el.syncId === listing.syncId),
        );
        if (foundIndex === -1) {
          listings.push(listing);
        } else {
          listings.splice(foundIndex, 1, listing);
        }
      }
    });

    return {
      ...state,
      listings,
      error: undefined,
      loaded: true,
    };
  }),

  on(ListingActions.clearListings, (state) => {
    return { ...state, listings: [], error: undefined };
  }),
  on(ListingActions.listingRemoved, (state, action) => {
    const listing = action.listing;

    const listingState: ListingState = {
      ...state,
      listings: [...state.listings],
      error: undefined,
    };

    // Find listing by ID first then by SyncId, Replace with the updated
    const foundIndex = listingState.listings.findIndex(
      (el) =>
        (el.id && listing.id && el.id === listing.id) || (el.syncId && listing.syncId && el.syncId === listing.syncId),
    );
    if (foundIndex !== -1) {
      listingState.listings.splice(foundIndex, 1);
    }

    return listingState;
  }),

  on(ListingActions.listingEnabled, (state, action) => {
    const listing = { ...action.listing, available: true };

    const listingState: ListingState = {
      ...state,
      listings: [...state.listings],
      error: undefined,
    };

    // Find listing by ID first then by SyncId
    const foundIndex = listingState.listings.findIndex(
      (el) =>
        (el.id && listing.id && el.id === listing.id) || (el.syncId && listing.syncId && el.syncId === listing.syncId),
    );

    if (foundIndex !== -1) {
      listingState.listings.splice(foundIndex, 1, listing);
    } else {
      listingState.listings.push(listing);
    }

    return listingState;
  }),
  on(ListingActions.listingDisabled, (state, action) => {
    const listing = { ...action.listing, available: false };

    const listingState: ListingState = {
      ...state,
      listings: [...state.listings],
      error: undefined,
    };

    // Find listing by ID first then by SyncId
    const foundIndex = listingState.listings.findIndex(
      (el) => (el.id && listing.id && el.id === listing.id) || (el.syncId && listing.syncId && el.syncId === listing.syncId),
    );

    if (foundIndex !== -1) {
      listingState.listings.splice(foundIndex, 1, listing);
    } else {
      listingState.listings.push(listing);
    }

    return listingState;
  }),
  on(ListingActions.listingFeaturedSet, (state, action) => {
    const listing = { ...action.listing };

    const listingState: ListingState = {
      ...state,
      listings: [...state.listings],
      error: undefined,
    };

    // Find listing by ID first then by SyncId
    const foundIndex = listingState.listings.findIndex(
      (el) =>
        (el.id && listing.id && el.id === listing.id) || (el.syncId && listing.syncId && el.syncId === listing.syncId),
    );

    if (foundIndex !== -1) {
      listingState.listings.splice(foundIndex, 1, listing);
    } else {
      listingState.listings.push(listing);
    }

    return listingState;
  }),
  on(ListingActions.selectListingByListing, (state, action) => {
    if (!action.listing || (!action.listing.id && !action.listing.syncId)) {
      return {
        ...state,
        selected: undefined,
        error: undefined,
      };
    }

    const id: number | string = action.listing.id ?? action.listing.syncId ?? uuidv4();
    return {
      ...state,
      selected: id,
      error: undefined,
    };
  }),
  on(ListingActions.listingSelected, (state, action) => {
    if (!action.listing || (!action.listing.id && !action.listing.syncId)) {
      return {
        ...state,
        selected: undefined,
        error: undefined,
      };
    }

    const id: number | string = action.listing.id ?? action.listing.syncId ?? uuidv4();
    return {
      ...state,
      selected: id,
      error: undefined,
    };
  }),
  on(ListingActions.updateListingFailed, (state, action) => {
    return {
      ...state,
      error: action.error,
    };
  }),
  on(ListingActions.listingFavorFailed, (state, action) => {
    return {
      ...state,
      error: action.error,
    };
  }),
  on(ListingActions.updatePricingFailed, (state, action) => {
    return {
      ...state,
      error: action.error,
    };
  }),
  on(ListingActions.listingFavorCompleted, (state) => {
    return {
      ...state,
      error: undefined,
    };
  }),
  on(ListingActions.addNewSiteSuccess, (state, action) => {
    const listings = state.listings.slice();
    const index = state.listings.findIndex((listing) => listing.id === action.listing.id);
    if (index !== -1) {
      listings.push(action.listing);
    } else {
      listings.splice(index, 1, action.listing);
    }

    return {
      ...state,
      listings,
      error: undefined,
    };
  }),
  on(SiteActions.siteLoaded, (state, action) => {
    if (!action.site?.listing?.id) {
      return state;
    }

    const site = action.site;

    const listings = state.listings.slice();
    const listingIndex = listings.findIndex((listing) => listing.id === action.site?.listing?.id);

    if (listingIndex >= 0) {
      const listing = { ...listings[listingIndex] };
      const siteIndex = listing.sites?.findIndex((existingSite) => existingSite.id === site.id);

      if (typeof siteIndex === 'number' && siteIndex >= 0 && listing.sites) {
        const sites = [...listing.sites];
        sites[siteIndex] = site;
        listing.sites = sites;
      } else {
        listing.sites = [...(listing.sites ?? []), site];
      }

      listings[listingIndex] = listing;

      return {
        ...state,
        listings,
        error: undefined,
      };
    }

    return state;
  }),
);
